import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, ShieldCheck, School, MapPin } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1587323655395-b1c77a12c89a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxzcGVlY2glMjB0aGVyYXBpc3QlMjBjaGlsZCUyMHRoZXJhcHl8ZW58MHx8fHwxNzg5OTcxNzk4fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["Every child", "deserves", "to be heard."];

const TRUST = [
  { icon: ShieldCheck, label: "RCI Registered · A58697" },
  { icon: School, label: "In-School Delivery" },
  { icon: MapPin, label: "Serving North Bengaluru" },
];

export const Hero = ({ onBook, onNavigate }) => {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 90]);
  const glowY = useTransform(scrollY, [0, 700], [0, -60]);

  return (
    <section id="top" className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24">
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -right-40 top-24 h-[480px] w-[480px] rounded-full bg-honey/25 blur-[120px]"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-7 font-mono text-xs font-medium uppercase tracking-[0.28em] text-pine-700"
            data-testid="hero-eyebrow"
          >
            Speech & Language Therapy for Schools
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl font-semibold leading-[1.06] tracking-tight text-pine-950 sm:text-5xl lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "italic text-clay" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg"
            data-testid="hero-subcopy"
          >
            RCI-registered speech and language therapy, delivered in your school.
            No travel for parents. No disruption for you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-booking-cta"
              onClick={onBook}
              className="group flex items-center gap-2 rounded-full bg-pine-900 px-8 py-4 text-sm font-semibold text-cream shadow-lg shadow-pine-900/20 transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:bg-pine-800 hover:shadow-xl"
            >
              Book a free consult
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-services-cta"
              onClick={() => onNavigate("services")}
              className="group flex items-center gap-2 rounded-full border border-pine-900/20 px-8 py-4 text-sm font-semibold text-pine-900 transition-[border-color,background-color] duration-300 hover:border-pine-900/40 hover:bg-sand"
            >
              What we offer schools
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-trust-tags"
          >
            {TRUST.map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-pine-700/80">
                <Icon size={15} className="text-clay" />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-5"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="overflow-hidden rounded-[999px_999px_28px_28px] border border-pine-900/10 shadow-2xl shadow-pine-900/15">
              <img
                src={HERO_IMG}
                alt="A child in an in-school speech therapy session"
                className="aspect-[4/5] w-full object-cover"
                data-testid="hero-image"
              />
              <div className="pointer-events-none absolute inset-0 rounded-[999px_999px_28px_28px] bg-gradient-to-t from-pine-950/25 via-transparent to-transparent" />
            </div>

            <div
              data-testid="hero-live-badge"
              className="absolute -left-4 bottom-10 flex items-center gap-4 rounded-2xl border border-pine-900/10 bg-white/90 px-5 py-4 shadow-xl backdrop-blur-md sm:-left-10"
            >
              <div className="flex h-9 items-end gap-[3px]">
                {[10, 18, 26, 14, 30, 20, 12].map((h, i) => (
                  <span
                    key={i}
                    className="wave-bar w-[3px] rounded-full bg-clay"
                    style={{ height: `${h}px`, animationDelay: `${i * 0.12}s` }}
                  />
                ))}
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-pine-700/70">In your school</p>
                <p className="text-sm font-semibold text-pine-950">Screen · Support · Report</p>
              </div>
            </div>

            <div className="absolute -right-3 top-8 rounded-full border border-honey/60 bg-honey/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-pine-900 backdrop-blur-sm sm:-right-6">
              RCI Reg. No. A58697
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
